import React from 'react';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import { client } from '../../utils/createApolloClient';
import { PostListingDocument, usePostListingQuery } from '../../graphql/api';
import RelatedPosts from '@apps/components/blog/related_posts';
import moment from 'moment';
import { SectionHeader } from '@apps/components/section-header';
import Head from 'next/head';

const BlogDetail = ({ post_slug = null, data = null }) => {
  // ========================= VARIABLES
  const router = useRouter();
  const metaTitle = `${data?.attributes?.post_title ?? post_slug} - Alchemis`;
  const metaDesc = data?.attributes?.post_short_description ?? '';
  const metaImg = `/images/meta/general.png`;
  const postData = data?.attributes;
  let postFilter = {
    filters: {
      companies: {
        slug: {
          eq: 'alchemis'
        }
      },
      post_type: {
        post_type_slug: {
          eq: 'blog'
        }
      }
    }
  };
  const { data: postListingData, loading } = usePostListingQuery({
    variables: {
      ...postFilter
    }
  });

  let blog_list = (postListingData?.posts?.data?.length > 0) ? postListingData?.posts?.data.map((e) => {
    return {
      id: e.id,
      title: e?.attributes?.post_title,
      text: e?.attributes?.post_short_description,
      image: e?.attributes?.post_thumb?.data?.attributes?.url ?? '/images/blog/post_0.png',
      slug: e?.attributes?.post_slug,
      date: e?.attributes?.publishedAt ? moment(e?.attributes?.publishedAt).format('DD MMM YYYY') : moment().format('DD MMM YYYY')
    };
  }) : [];
  blog_list = blog_list.filter((item) => item.slug !== post_slug);

  // ========================= EVENTS
  const handleTagClick = (slug) => {
    router.push(
      {
        pathname: `/blog`,
        query: { tag: slug }
      },
      undefined,
      { scroll: false }
    );
  };

  // ========================= VIEWS
  return (
    <div>
      <Head>
        {/* Add the JSON-LD script */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              mainEntityOfPage: {
                "@type": "WebPage",
                "@id": router.asPath,
              },
              headline: postData?.post_title,
              description: postData?.post_short_description,
              datePublished: postData?.publishedAt ? moment(postData?.publishedAt).format('DD MMM YYYY') : moment().format('DD MMM YYYY'),
            }),
          }}
        />
      </Head>
      <NextSeo
        title={metaTitle}
        description={metaDesc}
        openGraph={{
          title: `${metaTitle}`,
          description: `${metaDesc}`,
          images: [
            {
              url: `${metaImg}`
            }
          ]
        }}
      />
      <div className='pt-[5.5rem] lg:pt-24'>
        {/* <!-- Post --> */}
        {postData && (
          <section className='relative py-16 lg:py-24'>
            <picture className='pointer-events-none absolute inset-0 -z-10 dark:hidden'>
              <img src='/images/gradient_light.jpg' alt='gradient' className='h-full w-full' />
            </picture>
            <div className='container relative'>
              <div className='block lg:flex relative'>
                <div className='blog-container'>
                  <header className='mb-10'>
                    <div className='text-jacarta-400 text-sm flex items-center gap-1 mb-5'>
                      <a href={`/blog`} className='post-category'>Blog</a>
                      <img className={'inline-block'} height={14} width={14}
                           src={'/images/svg-icon/chevron-right.svg'} />
                      <a href={`/blogs/${postData?.post_categories?.data?.[0]?.attributes?.post_category_slug}`}
                         className='post-category'>{data?.attributes?.post_categories?.data?.[0]?.attributes?.post_category_name}</a>
                      <img className={'inline-block'} height={14} width={14}
                           src={'/images/svg-icon/chevron-right.svg'} />
                      <p>{data?.attributes?.post_title}</p>
                    </div>
                    <h1 className='font-display text-jacarta-700 text-2xl dark:text-white sm:text-5xl'>
                      {postData?.post_title}
                    </h1>
                  </header>

                  <article className='mb-12'>
                    <div className='article-content'>
                      <div className='article-content-wide'>
                        <div className='blog-content'
                             dangerouslySetInnerHTML={{ __html: postData?.post_description }} />
                      </div>
                    </div>
                  </article>
                </div>
                <div>
                  <div className='tag-container sticky top-[100px]'>
                    <h6 className='text-xl font-bold mb-8'>Post Info</h6>
                    <div className='mb-6'>
                      <p>Category:</p>
                      <a href={`/blogs/${postData?.post_categories?.data?.[0]?.attributes?.post_category_slug}`}
                         className='post-category'>{data?.attributes?.post_categories?.data?.[0]?.attributes?.post_category_name}</a>
                    </div>
                    <div>
                      <p>Tags:</p>
                      <div className='flex flex-wrap gap-2 mt-2'>
                        {postData?.post_tags?.data?.map((tag) => {
                          const { post_tag_name, post_tag_slug } = tag.attributes;
                          return (
                            <button
                              key={post_tag_slug}
                              className={`group flex items-center border-primary text-white border px-4 py-2 font-display text-sm font-semibold transition-colors hover:border-transparent hover:bg-primary hover:text-white dark:text-white dark:hover:border-transparent dark:hover:bg-primary dark:hover:text-white`}
                              onClick={() => handleTagClick(post_tag_slug)}
                            >
                              <span className='whitespace-nowrap'>{post_tag_name}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className='mt-[58px]'>
                <SectionHeader
                  containerClass={'mb-8'}
                  title={'Related Posts'}
                  titleProps={{
                    style: {
                      textAlign: 'left'
                    }
                  }}
                />
              </div>
            </div>
            <div className="pl-7 w-screen features-section overflow-hidden">
              {!loading && (
                <RelatedPosts data={blog_list} breakpoint={{
                  1440: {
                    slidesPerView: 4.5
                  },
                  1024: {
                    slidesPerView: 3.5
                  },
                  768: {
                    slidesPerView: 2.5
                  },
                  375: {
                    slidesPerView: 1.5
                  }
                }} />
              )}
            </div>
          </section>
        )}
        {/* <!-- end post --> */}
      </div>
    </div>
  );
};

export async function getStaticPaths() {
  const { data } = await client.query({
    query: PostListingDocument,
    variables: {
      filters: {
        companies: {
          slug: {
            eq: 'alchemis'
          }
        },
        post_type: {
          post_type_slug: {
            eq: 'blog'
          }
        }
      }
    }
  });
  const paths = data?.posts?.data?.map((post) => ({
    params: {
      slug: post?.attributes?.post_slug
    }
  }));

  return {
    paths,
    fallback: false
  };
};

export const getStaticProps = async ({ params }) => {
  const post_slug = params?.slug ?? '';

  const post_listing_query = client.query({
    query: PostListingDocument,
    variables: {
      filters: {
        post_slug: {
          eq: post_slug
        },
        companies: {
          slug: {
            eq: 'alchemis'
          }
        },
        post_type: {
          post_type_slug: {
            eq: 'blog'
          }
        }
      }
    }
  });
  const responses = await Promise.all([post_listing_query]);

  return {
    props: {
      post_slug: post_slug,
      data: responses[0]?.data?.posts?.data[0]
    }
  };
};

export default BlogDetail;
